import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Play, Pause, Volume2, Sparkles, Music } from 'lucide-react';
import type { PodcastWithRelations } from '../../../hooks/usePodcasts';

interface PlayButtonProps {
  podcast: PodcastWithRelations;
  currentPodcast: PodcastWithRelations | null;
  isPlaying: boolean;
  loadingAudio: boolean;
  onPlayPause: (podcast: PodcastWithRelations) => void;
  size?: 'sm' | 'md' | 'lg';
  variant?: 'minimal' | 'full';
}

export const PlayButton: React.FC<PlayButtonProps> = ({
  podcast,
  currentPodcast,
  isPlaying,
  loadingAudio,
  onPlayPause,
  size = 'md',
  variant = 'full'
}) => {
  const isCurrentPodcast = currentPodcast?.id === podcast.id;
  const isLoading = loadingAudio && isCurrentPodcast;

  const sizeClasses = {
    sm: 'w-8 h-8',
    md: 'w-12 h-12',
    lg: 'w-16 h-16'
  };

  const iconSizes = {
    sm: 'w-4 h-4',
    md: 'w-6 h-6',
    lg: 'w-8 h-8'
  };

  return (
    <motion.button
      onClick={(e) => {
        e.preventDefault();
        onPlayPause(podcast);
      }}
      disabled={loadingAudio && !isCurrentPodcast}
      whileHover={{ scale: 1.03, boxShadow: '0 0 20px rgba(0, 149, 255, 0.3)' }}
      whileTap={{ scale: 0.98 }}
      className={`group relative flex items-center justify-center overflow-hidden cursor-pointer transform hover:-translate-y-1 ${
        loadingAudio && !isCurrentPodcast ? 'opacity-50 pointer-events-none' : ''
      } ${
        variant === 'full'
          ? 'gap-2 md:gap-3 px-4 md:px-6 py-3 md:py-4 bg-gradient-to-r from-primary to-blue-600 hover:from-blue-600 hover:to-primary text-primary-foreground'
          : 'bg-primary/10 hover:bg-primary/20'
      } rounded-full transition-all duration-300 disabled:opacity-50 shadow-lg hover:shadow-xl border border-primary/20`}
      aria-label={isCurrentPodcast && isPlaying ? 'Pause' : 'Play'}
    >
      {/* Subtle gradient overlay */}
      <div className="absolute inset-0 bg-gradient-to-r from-primary/10 to-blue-500/10 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
      
      {/* Animated highlight effect */}
      <motion.div 
        className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent"
        initial={{ x: '-100%' }}
        animate={{ x: '200%' }}
        transition={{ 
          repeat: Infinity, 
          duration: 1.5,
          ease: "easeInOut",
          repeatDelay: 1
        }}
      />

      <div className={`relative ${sizeClasses[size]} flex items-center justify-center`}>
        <AnimatePresence mode="wait">
          {isLoading ? (
            <motion.div
              key="loader"
              className="absolute inset-0"
              initial={{ opacity: 0, rotate: 0 }}
              animate={{ opacity: 1, rotate: 360 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
            >
              <svg className="w-full h-full" viewBox="0 0 50 50">
                <defs>
                  <linearGradient id="loader-gradient" x1="0%" y1="0%" x2="100%" y2="0%">
                    <stop offset="0%" stopColor="var(--primary)" stopOpacity="1" />
                    <stop offset="100%" stopColor="var(--primary)" stopOpacity="0.2" />
                  </linearGradient>
                </defs>
                <circle
                  cx="25"
                  cy="25"
                  r="20"
                  fill="none"
                  stroke="url(#loader-gradient)"
                  strokeWidth="4"
                  strokeLinecap="round"
                  strokeDasharray="80, 200"
                />
              </svg>
            </motion.div>
          ) : (
            <motion.div
              key="icon"
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ 
                scale: 1,
                opacity: 1
              }}
              exit={{ scale: 0.8, opacity: 0 }}
              transition={{ 
                type: "spring", 
                stiffness: 300, 
                damping: 15
              }}
              className="relative z-10 drop-shadow-[0_0_5px_rgba(0,149,255,0.5)]"
            >
              {isCurrentPodcast && isPlaying ? (
                <Pause className={`${iconSizes[size]} text-white`} />
              ) : (
                <Play className={`${iconSizes[size]} text-white`} />
              )}
            </motion.div>
          )}
        </AnimatePresence>

        <AnimatePresence>
          {isCurrentPodcast && isPlaying && !isLoading && (
            <motion.div
              className="absolute inset-0 flex items-center justify-center gap-1"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              {[...Array(3)].map((_, i) => (
                <motion.div
                  key={i}
                  className="absolute w-full h-full rounded-full border-2 border-primary/30"
                  initial={{ scale: 1, opacity: 0 }}
                  animate={{
                    scale: [1, 1.5, 2],
                    opacity: [0.5, 0.2, 0]
                  }}
                  transition={{
                    duration: 2,
                    repeat: Infinity,
                    delay: i * 0.4,
                    ease: "easeOut"
                  }}
                />
              ))}
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {variant === 'full' && (
        <div className="flex items-center gap-3">
          <motion.span
            className="font-medium text-white text-sm tracking-wide whitespace-nowrap drop-shadow-[0_0_2px_rgba(0,0,0,0.5)]"
            initial={false}
            animate={{ opacity: 1 }}
            whileHover={{ opacity: 0.9 }}
          >
            {isLoading
              ? 'A carregar...'
              : isCurrentPodcast && isPlaying
              ? 'A tocar agora'
              : (
                <span className="flex items-center gap-1.5">
                  <motion.div
                    animate={{ 
                      scale: [1, 1.1, 1],
                      rotate: [0, 3, -3, 0]
                    }}
                    transition={{ 
                      duration: 2,
                      repeat: Infinity,
                      repeatType: "reverse"
                    }}
                  >
                    <Sparkles className="w-3 h-3 text-yellow-300" />
                  </motion.div>
                  <span>Ouvir Episódio</span>
                </span>
              )}
          </motion.span>
          {isCurrentPodcast && isPlaying && (
            <motion.div
              initial={{ opacity: 0, x: -10 }}
              animate={{ opacity: 1, x: 0 }}
              className="flex items-center gap-2"
            >
              <motion.div
                animate={{ 
                  scale: [1, 1.1, 1],
                  opacity: [0.7, 1, 0.7]
                }}
                transition={{ 
                  duration: 1.5,
                  repeat: Infinity,
                  repeatType: "reverse"
                }}
              >
                <Volume2 className="w-4 h-4 text-primary-foreground" />
              </motion.div>
              <div className="hidden md:flex gap-0.5 ml-1">
                {[...Array(3)].map((_, i) => (
                  <motion.div
                    key={i}
                    className="w-0.5 h-3 bg-white rounded-full"
                    animate={{
                      height: ['12px', '4px', '12px']
                    }}
                    transition={{
                      duration: 0.8,
                      repeat: Infinity,
                      delay: i * 0.2
                    }}
                  />
                ))}
              </div>
            </motion.div>
          )}
        </div>
      )}
      
      {/* Subtle pulsing ring effect */}
      <motion.span 
        className="absolute -inset-1 rounded-full opacity-0 group-hover:opacity-100"
        animate={{ 
          boxShadow: [
            '0 0 0 0 rgba(0, 149, 255, 0)',
            '0 0 0 3px rgba(0, 149, 255, 0.2)',
            '0 0 0 0 rgba(0, 149, 255, 0)'
          ]
        }}
        transition={{ 
          duration: 1.5,
          repeat: Infinity,
          repeatType: "loop"
        }}
      />
    </motion.button>
  );
};